// File: webelieve-frontend/src/features/notes/components/NoteVersionHistory.tsx
// Version history drawer for note version comparison and restore

import React, { useState, useEffect } from 'react';
import * as notesApi from '../services/notesApi';
import type { Note, NoteVersion } from '../types/note.types';

interface NoteVersionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  noteId: string;
  onRestore: (note: Note) => void;
}

function formatVersionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const NoteVersionHistory: React.FC<NoteVersionHistoryProps> = ({
  isOpen,
  onClose,
  noteId,
  onRestore,
}) => {
  const [versions, setVersions] = useState<NoteVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<NoteVersion | null>(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (isOpen && noteId) {
      setLoading(true);
      notesApi
        .fetchVersionHistory(noteId)
        .then((versions) => {
          setVersions(versions);
          setSelectedVersion(null);
        })
        .catch(() => setVersions([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen, noteId]);

  const handleRestore = async () => {
    if (!selectedVersion || restoring) return;
    setRestoring(true);
    try {
      const restoredNote = await notesApi.restoreVersion(noteId, selectedVersion.version_id);
      onRestore(restoredNote);
      onClose();
    } catch {
      // error handled by caller
    } finally {
      setRestoring(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/30">
      <div className="w-[420px] bg-white dark:bg-gray-800 shadow-2xl flex flex-col h-full">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Version History</h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Version list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-12 px-6">
              <svg className="w-12 h-12 text-gray-200 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-gray-400">No version history yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {versions.map((version, index) => (
                <button
                  key={version.version_id}
                  onClick={() => setSelectedVersion(version)}
                  className={`w-full text-left px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    selectedVersion?.version_id === version.version_id
                      ? 'bg-lex-accent-soft border-l-2 border-l-lex-accent'
                      : 'border-l-2 border-l-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      v{version.version_number}
                      {index === 0 && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                          Latest
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                      {formatVersionDate(version.edited_at)}
                    </span>
                  </div>
                  {version.change_summary && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {version.change_summary}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      by {version.edited_by_name || 'Unknown'}
                    </span>
                    {version.fields_changed && version.fields_changed.length > 0 && (
                      <div className="flex items-center gap-1">
                        {version.fields_changed.slice(0, 3).map((field) => (
                          <span
                            key={field}
                            className="text-[9px] px-1 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded"
                          >
                            {field}
                          </span>
                        ))}
                        {version.fields_changed.length > 3 && (
                          <span className="text-[9px] text-gray-400">+{version.fields_changed.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected version preview + restore */}
        {selectedVersion && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4">
            <div className="mb-3">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Preview — v{selectedVersion.version_number}
              </h4>
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 max-h-32 overflow-y-auto whitespace-pre-wrap">
                {selectedVersion.content_text?.slice(0, 500) || selectedVersion.title || 'No content'}
                {(selectedVersion.content_text?.length ?? 0) > 500 && '...'}
              </div>
            </div>
            <button
              onClick={handleRestore}
              disabled={restoring}
              className="w-full px-4 py-2 text-sm font-medium bg-lex-accent hover:bg-lex-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-600 text-lex-accent-contrast rounded-lg transition-colors"
            >
              {restoring ? 'Restoring...' : `Restore v${selectedVersion.version_number}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoteVersionHistory;
